import { useState, useEffect, useCallback } from "react";
import {
  FolderOpen,
  Plus,
  Trash2,
  ArrowLeft,
  Loader2,
  ArrowRight,
  CalendarDays,
  Bot,
  Users,
  Sparkles,
  Settings,
  Zap,
  MousePointer,
  Play,
  CheckCircle2,
} from "lucide-react";
import { apiv2, ApiError, type ProjectListItem } from "../apiv2";
import { useAuth } from "../auth";
import GreenTendrils from "./GreenTendrils";

interface Props {
  onBack: () => void;
  onSelectProject: (projectId: string, executionMode: string) => void;
  onSetupProject: (projectId: string) => void;
}

export default function ProjectDashboardV2({
  onBack,
  onSelectProject,
  onSetupProject,
}: Props) {
  const { isAdmin, isProjectUser } = useAuth();
  const canCreate = isAdmin || isProjectUser;

  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // A name/slug collision with a project this user has no visibility into
  // (they can't see the tenant's other projects) — surfaced with the
  // existing project's owner instead of a bare "slug already exists" the
  // requester has no way to act on.
  const [slugConflict, setSlugConflict] = useState<{
    name: string | null;
    ownerName: string | null;
    ownerEmail: string | null;
  } | null>(null);

  // Quick-create
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newDomain, setNewDomain] = useState("");
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState<"all" | "in-flight" | "completed">(
    "all",
  );

  const loadProjects = useCallback(async () => {
    try {
      const list = await apiv2.listProjects();
      setProjects(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load projects");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setError(null);
    setSlugConflict(null);
    try {
      const proj = await apiv2.createProject({
        name: newName.trim(),
        description: newDesc.trim() || undefined,
        domain: newDomain.trim() || undefined,
      });
      setNewName("");
      setNewDesc("");
      setNewDomain("");
      setShowCreate(false);
      // New project → always go to mode selection first
      onSelectProject(proj.id, "");
    } catch (err) {
      const existingProject =
        err instanceof ApiError &&
        err.body &&
        typeof err.body === "object" &&
        "existing_project" in err.body
          ? (
              err.body as {
                existing_project: {
                  name: string | null;
                  owner_name: string | null;
                  owner_email: string | null;
                };
              }
            ).existing_project
          : null;
      if (existingProject) {
        setSlugConflict({
          name: existingProject.name,
          ownerName: existingProject.owner_name,
          ownerEmail: existingProject.owner_email,
        });
      } else {
        setError(
          err instanceof Error ? err.message : "Failed to create project",
        );
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!confirm("Delete this project?")) return;
    try {
      await apiv2.deleteProject(id);
      await loadProjects();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete");
    }
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return iso;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] relative">
        <div className="absolute inset-0 dot-grid opacity-40" />
        <Loader2
          size={24}
          className="animate-spin text-brand-500 relative z-10"
        />
      </div>
    );
  }

  return (
    <div className="overflow-y-auto custom-scrollbar h-full relative bg-surface-500">
      <div className="absolute inset-0 dot-grid" />
      <div className="glow-orb glow-orb-1" />
      <div className="glow-orb glow-orb-2" />
      <div className="glow-orb glow-orb-3" />
      <GreenTendrils />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-10 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4 animate-fade-in">
          <div className="flex items-center gap-5">
            <button
              onClick={onBack}
              className="p-2.5 rounded-xl border border-white/8 hover:border-brand-500/30 transition-all duration-300 hover:bg-brand-500/5"
              style={{ background: "rgba(28,28,42,0.5)" }}
            >
              <ArrowLeft size={18} className="text-white/50" />
            </button>
            <div>
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand-500/30 text-brand-400 text-xs font-medium mb-3"
                style={{ background: "rgba(134,188,37,0.08)" }}
              >
                <Sparkles size={12} />
                Project Workspace
              </div>
              <h1 className="text-4xl md:text-5xl font-black tracking-tight">
                <span className="gradient-text-green">Projects</span>
              </h1>
              <p className="text-sm text-white/40 mt-2">
                {projects.length} project{projects.length !== 1 ? "s" : ""}{" "}
                &middot; Select to configure or launch agents
              </p>
            </div>
          </div>
          {canCreate && (
            <button
              onClick={() => setShowCreate(!showCreate)}
              className="px-6 py-3 text-white rounded-xl text-sm font-bold transition-all duration-300 flex items-center gap-2"
              style={{
                background: "linear-gradient(135deg, #86BC25, #6B9B1E)",
                boxShadow:
                  "0 0 30px rgba(134,188,37,0.3), 0 4px 20px rgba(0,0,0,0.3)",
              }}
            >
              <Plus size={16} /> New Project
            </button>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-300 flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-300 text-xs ml-4"
            >
              dismiss
            </button>
          </div>
        )}

        {/* Name/slug collision with a project this user can't see — give them
            an actual next step instead of a bare "already exists" error. */}
        {slugConflict && (
          <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl text-sm text-amber-200 space-y-2">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <p className="font-semibold text-amber-100">
                  A project named "{slugConflict.name ?? newName.trim()}"
                  already exists in this tenant.
                </p>
                {slugConflict.ownerName ? (
                  <p className="text-amber-200/80">
                    It's owned by {slugConflict.ownerName}
                    {slugConflict.ownerEmail
                      ? ` (${slugConflict.ownerEmail})`
                      : ""}
                    . Ask them to add you as a team member from that project's
                    setup page, or contact a tenant admin.
                  </p>
                ) : (
                  <p className="text-amber-200/80">
                    Ask its owner to add you as a team member, or contact a
                    tenant admin.
                  </p>
                )}
              </div>
              <button
                onClick={() => setSlugConflict(null)}
                className="text-amber-500 hover:text-amber-300 text-xs shrink-0"
              >
                dismiss
              </button>
            </div>
            {slugConflict.ownerEmail && (
              <a
                href={`mailto:${slugConflict.ownerEmail}?subject=${encodeURIComponent(
                  `Add me to "${slugConflict.name ?? newName.trim()}"`,
                )}`}
                className="inline-flex items-center gap-1 text-xs font-medium text-amber-300 hover:text-amber-100 underline"
              >
                Email {slugConflict.ownerName ?? "the owner"} to request access
              </a>
            )}
          </div>
        )}

        {/* Quick Create */}
        {showCreate && (
          <div
            className="glass rounded-2xl p-6 space-y-4 animate-fade-in"
            style={{
              background: "rgba(28,28,42,0.6)",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Plus size={14} className="text-brand-500" /> New Project
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-white/40 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => {
                    setNewName(e.target.value);
                    setSlugConflict(null);
                  }}
                  placeholder="My Data Project"
                  className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs text-white/40 mb-1">
                  Domain
                </label>
                <input
                  type="text"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="e.g., Finance"
                  className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                />
              </div>
              <div>
                <label className="block text-xs text-white/40 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  placeholder="Brief description"
                  className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleCreate}
                disabled={creating || !newName.trim()}
                className="px-5 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-50 flex items-center gap-2"
                style={{
                  background: "linear-gradient(135deg, #86BC25, #6B9B1E)",
                }}
              >
                {creating ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Plus size={14} />
                )}
                Create & Setup
              </button>
              <button
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 rounded-lg text-xs text-white/50 hover:text-white/80 border border-white/8 hover:border-white/20 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Project cards grid */}
        {projects.length === 0 ? (
          <div className="text-center py-20">
            <FolderOpen size={48} className="mx-auto text-white/10 mb-4" />
            <h3 className="text-lg font-semibold text-white/30 mb-2">
              No projects yet
            </h3>
            <p className="text-sm text-white/20">
              Create your first project to get started with SI agents.
            </p>
          </div>
        ) : (
          <>
            {/* Filter tabs */}
            <div
              className="flex items-center gap-1 p-1 rounded-xl w-fit"
              style={{
                background: "rgba(28,28,42,0.5)",
                border: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              {[
                {
                  key: "all" as const,
                  label: "All Projects",
                  count: projects.length,
                  icon: <FolderOpen size={13} />,
                },
                {
                  key: "in-flight" as const,
                  label: "In Flight",
                  count: projects.filter((p) => p.inflight_count > 0).length,
                  icon: <Play size={13} />,
                },
                {
                  key: "completed" as const,
                  label: "Completed",
                  count: projects.filter(
                    (p) => p.completed_count > 0 && p.inflight_count === 0,
                  ).length,
                  icon: <CheckCircle2 size={13} />,
                },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setFilter(t.key)}
                  className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 flex items-center gap-1.5 ${
                    filter === t.key
                      ? "bg-brand-500/20 text-brand-400"
                      : "text-white/40 hover:text-white/60 hover:bg-white/5"
                  }`}
                >
                  {t.icon}
                  {t.label}
                  <span
                    className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[11px] font-bold ${
                      filter === t.key
                        ? "bg-brand-500/20 text-brand-400"
                        : "bg-white/5 text-white/30"
                    }`}
                  >
                    {t.count}
                  </span>
                </button>
              ))}
            </div>

            {(() => {
              const filtered =
                filter === "all"
                  ? projects
                  : filter === "in-flight"
                    ? projects.filter((p) => p.inflight_count > 0)
                    : projects.filter(
                        (p) => p.completed_count > 0 && p.inflight_count === 0,
                      );

              if (filtered.length === 0) {
                return (
                  <div className="text-center py-16">
                    {filter === "in-flight" ? (
                      <Play size={48} className="mx-auto text-white/10 mb-4" />
                    ) : (
                      <CheckCircle2
                        size={48}
                        className="mx-auto text-white/10 mb-4"
                      />
                    )}
                    <h3 className="text-lg font-semibold text-white/30 mb-2">
                      {filter === "in-flight"
                        ? "No projects with in-flight work"
                        : "No projects with only completed work"}
                    </h3>
                    <p className="text-sm text-white/20">
                      {filter === "in-flight"
                        ? "Start new work on a project to see it here."
                        : "Projects appear here once all work is completed."}
                    </p>
                  </div>
                );
              }

              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filtered.map((p) => (
                    <div
                      key={p.id}
                      className="group glass rounded-2xl p-5 cursor-pointer transition-all duration-300 hover:border-brand-500/30"
                      style={{
                        background: "rgba(28,28,42,0.5)",
                        border: "1px solid rgba(255,255,255,0.05)",
                      }}
                      onClick={() => onSelectProject(p.id, p.execution_mode)}
                    >
                      {/* Top */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-white truncate group-hover:text-brand-400 transition">
                            {p.name}
                          </h3>
                          {p.domain && (
                            <span className="text-[11px] text-brand-400/80 bg-brand-500/10 px-2 py-0.5 rounded-full mt-1 inline-block">
                              {p.domain}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onSetupProject(p.id);
                            }}
                            className="p-1.5 rounded-lg text-white/30 hover:text-brand-500 hover:bg-brand-500/10 transition"
                            title="Project Setup"
                          >
                            <Settings size={14} />
                          </button>
                          {(isAdmin || isProjectUser) && (
                            <button
                              onClick={(e) => handleDelete(e, p.id)}
                              className="p-1.5 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 transition"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Description */}
                      {p.description && (
                        <p className="text-xs text-white/30 line-clamp-2 mb-3">
                          {p.description}
                        </p>
                      )}

                      {/* Activity badges */}
                      {(p.inflight_count > 0 || p.completed_count > 0) && (
                        <div className="flex items-center gap-2 mb-3">
                          {p.inflight_count > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium text-sky-400 bg-sky-500/10">
                              <Play size={9} /> {p.inflight_count} in flight
                            </span>
                          )}
                          {p.completed_count > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium text-green-400 bg-green-500/10">
                              <CheckCircle2 size={9} /> {p.completed_count}{" "}
                              completed
                            </span>
                          )}
                        </div>
                      )}

                      {/* Stats bar */}
                      <div className="flex items-center gap-4 text-[11px] text-white/30 pt-3 border-t border-white/5">
                        <span
                          className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full ${
                            p.execution_mode === "autonomous"
                              ? "text-purple-400/80 bg-purple-500/10"
                              : "text-sky-400/80 bg-sky-500/10"
                          }`}
                        >
                          {p.execution_mode === "autonomous" ? (
                            <Zap size={9} />
                          ) : (
                            <MousePointer size={9} />
                          )}
                          {p.execution_mode === "autonomous"
                            ? "Auto"
                            : "Direct"}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users size={10} /> {p.member_count}
                        </span>
                        <span className="flex items-center gap-1">
                          <Bot size={10} /> {p.agent_count} agents
                        </span>
                        <span className="flex items-center gap-1">
                          <ArrowRight size={10} /> {p.run_count} runs
                        </span>
                        <span className="flex-1" />
                        <span className="flex items-center gap-1">
                          <CalendarDays size={10} /> {formatDate(p.created_at)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
}
